import { ColumnDef } from '@tanstack/react-table'
import { GlobalDocument } from '@/api/documents'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from '@tanstack/react-router'
import i18n from '@/i18n'
import { formatBytes } from '@/lib/utils'
import { timestamp_to_date } from '@/utils/time'
import { formatDistanceToNow } from 'date-fns'
import { ar, fr } from 'date-fns/locale'
import { FileTextIcon, ImageIcon, FileSpreadsheetIcon, FileIcon, EyeIcon, DownloadIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { get_case_file_download_link } from '@/api/case'
import { FileViewer } from '@/components/file-viewer'
import { toast } from '@/hooks/use-toast'

function getFileIcon(contentType: string) {
  if (contentType === 'application/pdf') return <FileTextIcon className="h-4 w-4 text-red-500" />
  if (contentType.startsWith('image/')) return <ImageIcon className="h-4 w-4 text-blue-500" />
  if (contentType.includes('spreadsheet') || contentType.includes('excel'))
    return <FileSpreadsheetIcon className="h-4 w-4 text-green-600" />
  if (contentType.includes('word') || contentType.includes('document'))
    return <FileTextIcon className="h-4 w-4 text-blue-600" />
  return <FileIcon className="h-4 w-4 text-muted-foreground" />
}

function getFileTypeBadge(contentType: string) {
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType.startsWith('image/')) return 'Image'
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return 'Excel'
  if (contentType.includes('word') || contentType.includes('document')) return 'Word'
  if (contentType.includes('zip') || contentType.includes('rar')) return 'Archive'
  const parts = contentType.split('/')
  return parts[parts.length - 1]?.toUpperCase()?.substring(0, 6) ?? 'File'
}

function DocumentActions({ file }: { file: GlobalDocument }) {
  const [loading, setLoading] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [fileUrl, setFileUrl] = useState('')

  const isPdf = file.content_type === 'application/pdf'
  const isImage = file.content_type.startsWith('image/')
  const isViewable = isPdf || isImage

  const fetchUrl = async (): Promise<string> => {
    if (fileUrl) return fileUrl
    const url = await get_case_file_download_link(file.id)
    setFileUrl(url)
    return url
  }

  const handlePreview = async () => {
    setLoading(true)
    try {
      const url = await fetchUrl()
      setFileUrl(url)
      setViewerOpen(true)
    } catch {
      toast({ title: i18n.t('error'), description: i18n.t('view_failed'), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    setLoading(true)
    try {
      const url = await fetchUrl()
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', file.original_filename)
      link.setAttribute('rel', 'noopener noreferrer')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      toast({ title: i18n.t('error'), description: i18n.t('download_failed'), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-1">
        {isViewable && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={i18n.t('view')}
            onClick={handlePreview}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeIcon className="h-4 w-4" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title={i18n.t('download')}
          onClick={handleDownload}
          disabled={loading}
        >
          {loading && !isViewable ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadIcon className="h-4 w-4" />}
        </Button>
      </div>

      <FileViewer
        isOpen={viewerOpen}
        onOpenChange={setViewerOpen}
        fileName={file.original_filename}
        fileUrl={fileUrl}
        fileType={file.content_type}
        isLoading={loading}
      />
    </>
  )
}

export function getColumns(fullText: boolean = false): ColumnDef<GlobalDocument>[] {
  const cols: ColumnDef<GlobalDocument>[] = [
    {
      accessorKey: 'original_filename',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n.t('doc_col_filename')} />
      ),
      cell: ({ row }) => {
        const { original_filename, content_type } = row.original
        return (
          <div className="flex items-center gap-2 max-w-[280px]">
            {getFileIcon(content_type)}
            <span className="truncate font-medium" title={original_filename}>
              {original_filename}
            </span>
          </div>
        )
      },
      enableSorting: true,
    },
    {
      accessorKey: 'content_type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n.t('doc_col_type')} />
      ),
      cell: ({ row }) => (
        <Badge variant="secondary" className="text-xs font-normal">
          {getFileTypeBadge(row.original.content_type)}
        </Badge>
      ),
      enableSorting: false,
    },
    // Snippet column — only in full-text search mode
    ...(fullText ? [{
      id: 'search_snippet',
      header: () => <span className="text-sm font-medium">{i18n.t('search_snippet')}</span>,
      cell: ({ row }: { row: any }) => {
        const { search_snippet } = row.original
        if (!search_snippet) return <span className="text-muted-foreground text-xs italic">{i18n.t('no_match_context')}</span>
        return (
          <p
            className="text-xs text-muted-foreground line-clamp-3 leading-relaxed [&_mark]:bg-yellow-200 [&_mark]:text-yellow-900 [&_mark]:rounded [&_mark]:px-0.5 dark:[&_mark]:bg-yellow-800 dark:[&_mark]:text-yellow-100 max-w-[320px]"
            dangerouslySetInnerHTML={{ __html: search_snippet }}
          />
        )
      },
      enableSorting: false,
      enableHiding: false,
    }] : []),
    {
      accessorKey: 'file_size',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n.t('doc_col_size')} />
      ),
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{formatBytes(row.original.file_size)}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: 'case_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n.t('doc_col_case')} />
      ),
      cell: ({ row }) => (
        // @ts-ignore
        <Link to={`/cases/${row.original.case}`} className="text-blue-600 hover:underline truncate max-w-[200px] block">
          {row.original.case_name}
        </Link>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'uploaded_by',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n.t('doc_col_uploaded_by')} />
      ),
      cell: ({ row }) => (
        <span className="text-sm">{row.original.uploaded_by?.name ?? '—'}</span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n.t('doc_col_date')} />
      ),
      cell: ({ row }) => {
        const d = timestamp_to_date(row.original.created_at)
        const isAr = i18n.language === 'ar'
        const isFr = i18n.language === 'fr'

        let dateLocale: any = undefined
        if (isAr) dateLocale = ar
        else if (isFr) dateLocale = fr

        return (
          <span className="text-sm text-muted-foreground" title={d.toLocaleString()}>
            {formatDistanceToNow(d, { addSuffix: true, locale: dateLocale })}
          </span>
        )
      },
      enableSorting: true,
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) => <DocumentActions file={row.original} />,
      enableSorting: false,
      enableHiding: false,
    },
  ]

  return cols
}
